import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container, Box, Typography, Tabs, Tab, Button, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Snackbar, Alert, Chip, Tooltip
} from "@mui/material";
import { Add, Edit, Delete, ArrowBack } from "@mui/icons-material";

const API = process.env.REACT_APP_API_URL;
const FIRMS = ["Datachef", "Techsahyogi"];

// Config for each master: how to load, save, and which fields to edit.
const MASTERS = [
  {
    key: "services",
    label: "Services",
    list: `${API}/api/codes`,
    base: `${API}/api/codes`,
    columns: [
      { key: "item", label: "Service / Item" },
      { key: "hsn_code", label: "HSN / SAC Code" },
    ],
    fields: [
      { key: "item", label: "Service / Item", type: "text", required: true },
      { key: "hsn_code", label: "HSN / SAC Code", type: "number" },
    ],
    empty: { item: "", hsn_code: "" },
  },
  {
    key: "employees",
    label: "Employees",
    list: `${API}/api/users/manage`,
    base: `${API}/api/users`,
    columns: [
      { key: "name", label: "Name" },
      { key: "mobile_no", label: "Mobile No." },
      { key: "role", label: "Role", render: (v) => <Chip size="small" label={v} color={v === "admin" ? "primary" : "default"} /> },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "mobile_no", label: "Mobile No.", type: "text", required: true },
      { key: "role", label: "Role", type: "select", options: ["user", "admin"] },
    ],
    empty: { name: "", mobile_no: "", role: "user" },
  },
  {
    key: "clients",
    label: "Clients",
    list: `${API}/api/clients`,
    base: `${API}/api/clients`,
    columns: [
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "firm", label: "Firm", render: (v) => <Chip size="small" label={v || "Datachef"} color={v === "Techsahyogi" ? "warning" : "info"} /> },
    ],
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "email", label: "Email", type: "text" },
      { key: "phone", label: "Phone", type: "text" },
      { key: "firm", label: "Firm", type: "select", options: FIRMS },
    ],
    empty: { name: "", email: "", phone: "", firm: "Datachef" },
  },
];

const Masters = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode: 'add'|'edit', values }
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  const cfg = MASTERS[tab];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(cfg.list);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRows([]);
      setToast({ open: true, msg: `Failed to load ${cfg.label}`, severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [cfg]);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/"); return; }
    load();
  }, [load, navigate]);

  const openAdd = () => setDialog({ mode: "add", values: { ...cfg.empty } });
  const openEdit = (row) => setDialog({ mode: "edit", values: { ...cfg.empty, ...row } });

  const handleSave = async () => {
    const v = dialog.values;
    const missing = cfg.fields.filter(f => f.required && !String(v[f.key] ?? "").trim());
    if (missing.length) {
      setToast({ open: true, msg: `${missing[0].label} is required`, severity: "error" });
      return;
    }
    try {
      if (dialog.mode === "add") {
        await axios.post(cfg.base, v);
      } else {
        await axios.put(`${cfg.base}/${v.id}`, v);
      }
      setDialog(null);
      setToast({ open: true, msg: `${cfg.label.replace(/s$/, "")} saved`, severity: "success" });
      load();
    } catch (err) {
      setToast({ open: true, msg: err.response?.data?.error || "Save failed", severity: "error" });
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name || row.item}"?`)) return;
    try {
      await axios.delete(`${cfg.base}/${row.id}`);
      setToast({ open: true, msg: "Deleted", severity: "success" });
      load();
    } catch (err) {
      setToast({ open: true, msg: err.response?.data?.error || "Delete failed", severity: "error" });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton onClick={() => navigate("/admin")}><ArrowBack /></IconButton>
          <Typography variant="h4" fontWeight={700} color="primary">Masters</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}>
          Add {cfg.label.replace(/s$/, "")}
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
          {MASTERS.map(m => <Tab key={m.key} label={m.label} />)}
        </Tabs>

        <TableContainer sx={{ maxHeight: "65vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {cfg.columns.map(c => (
                  <TableCell key={c.key} sx={{ fontWeight: 700, bgcolor: "grey.50" }}>{c.label}</TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "grey.50" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={cfg.columns.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    {loading ? "Loading…" : `No ${cfg.label.toLowerCase()} yet. Click “Add ${cfg.label.replace(/s$/, "")}”.`}
                  </TableCell>
                </TableRow>
              )}
              {rows.map(row => (
                <TableRow key={row.id} hover>
                  {cfg.columns.map(c => (
                    <TableCell key={c.key}>{c.render ? c.render(row[c.key]) : (row[c.key] ?? "—")}</TableCell>
                  ))}
                  <TableCell align="right">
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(row)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / Edit dialog */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{dialog?.mode === "add" ? "Add" : "Edit"} {cfg.label.replace(/s$/, "")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {dialog && cfg.fields.map(f => (
              f.type === "select" ? (
                <TextField
                  key={f.key} select label={f.label} value={dialog.values[f.key] ?? ""}
                  onChange={e => setDialog(d => ({ ...d, values: { ...d.values, [f.key]: e.target.value } }))}
                  fullWidth
                >
                  {f.options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              ) : (
                <TextField
                  key={f.key} label={f.label} type={f.type} value={dialog.values[f.key] ?? ""}
                  required={f.required}
                  onChange={e => setDialog(d => ({ ...d, values: { ...d.values, [f.key]: e.target.value } }))}
                  fullWidth
                />
              )
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open} autoHideDuration={3000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Masters;
